import authEndpoints from '@/lib/api/endpoints/authEndpoints';
import type { LoginModel, ChangePasswordModel, ResetPasswordModel } from '@/lib/models/auth';
import {
	ChangePasswordModelSchema,
	ResetPasswordModelSchema,
	AuthenticateResponseSchema,
	ApplicationUserDtoSchema,
} from '@/lib/models/auth';
import { zodValidator } from '@/lib/utils/zodValidator';
import { useAuthStore } from '@/stores/auth';
import routerFactory from '@/lib/nuxtGlobalRouter';

/**
 * Interfaccia per i dati di login del service (formato interno del form)
 */
export interface LoginServiceData {
	username: string;
	password: string;
}

/**
 * Interfaccia per i dati di reset password del service (formato interno del form)
 */
export interface ResetPasswordServiceData {
	username: string;
	token: string;
	newPassword: string;
}

/**
 * Interfaccia per i dati di cambio password del service (formato interno del form)
 */
export interface ChangePasswordServiceData {
	oldPassword: string;
	newPassword: string;
}

/**
 * Interfaccia per il service di autenticazione
 * Gestisce la logica di business per l'autenticazione e la gestione utenti
 */
interface AuthService {
	/**
	 * Ping autenticato per verificare se l'utente è ancora loggato
	 * @returns Promise void
	 */
	authenticatedPing(): Promise<void>;

	/**
	 * Login utente con gestione automatica del token nello store
	 * @param data - Dati di login (username e password)
	 * @returns Promise<boolean> - true se login riuscito, false altrimenti
	 */
	login(data: LoginServiceData): Promise<boolean>;

	/**
	 * Refresh del token di autenticazione
	 * @returns Promise<boolean> - true se refresh riuscito, false altrimenti
	 */
	refreshToken(): Promise<boolean>;

	/**
	 * Logout utente con pulizia store e redirect
	 * @param go - Se effettuare il redirect alla login (default true)
	 */
	logout(go?: boolean): void;

	/**
	 * Invia email per il reset della password
	 * @param username - Username per cui inviare il reset
	 * @returns Promise void
	 */
	sendResetPassword(username: string): Promise<void>;

	/**
	 * Reset della password con token
	 * @param data - Dati per il reset password
	 * @returns Promise void
	 */
	resetPassword(data: ResetPasswordServiceData): Promise<void>;

	/**
	 * Recupera lo username di un utente per ID
	 * @param key - ID dell'utente
	 * @returns Promise<string> - Username dell'utente
	 */
	getUserName(key: string): Promise<string>;

	/**
	 * Cambio password utente
	 * @param payload - Dati per il cambio password
	 * @returns Promise void
	 */
	changePassword(payload: ChangePasswordServiceData): Promise<void>;

	/**
	 * Impersona un utente tramite workerId
	 * @param workerId - ID del lavoratore da impersonare
	 * @returns Promise void
	 */
	impersonate(workerId: string): Promise<void>;

	/**
	 * Termina l'impersonazione
	 */
	unImpersonate(): void;

	/**
	 * Recupera e salva nello store le informazioni dell'utente corrente
	 * @returns Promise void
	 */
	getUserInfo(): Promise<void>;
}

/**
 * Implementazione del service di autenticazione
 * Mantiene compatibilità con il JavaScript originale e aggiunge validazione runtime
 */
const authService: AuthService = {
	async authenticatedPing(): Promise<void> {
		return (await authEndpoints.authenticatedPing()).data;
	},

	async login(data: LoginServiceData): Promise<boolean> {
		// Usa i composable moderni per store e router
		const authStore = useAuthStore();

		// Converte i dati del form nel formato richiesto dall'endpoint
		const loginData: LoginModel = {
			username: data.username,
			password: data.password,
		};

		const authenticationResponse = (await authEndpoints.login(loginData)).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateSingle(
			authenticationResponse,
			AuthenticateResponseSchema,
			'AuthenticateResponse.login',
		);

		if (authenticationResponse) {
			// Usa le actions Pinia invece delle mutations Vuex
			authStore.setAccessToken(authenticationResponse.token);
			authStore.setRefreshToken(authenticationResponse.refreshToken.token);
			await this.getUserInfo();
			return true;
		}
		return false;
	},

	async refreshToken(): Promise<boolean> {
		// Usa il composable moderno per lo store Pinia
		const authStore = useAuthStore();
		const refreshTokenString = authStore.$state.refreshToken;

		if (refreshTokenString) {
			const authenticationResponse = (await authEndpoints.refreshToken(refreshTokenString)).data;
			// Validazione runtime della response (attiva solo in development)
			zodValidator.validateSingle(
				authenticationResponse,
				AuthenticateResponseSchema,
				'AuthenticateResponse.refreshToken',
			);

			if (authenticationResponse) {
				// Usa le actions Pinia invece delle mutations Vuex
				authStore.setAccessToken(authenticationResponse.token);
				authStore.setRefreshToken(authenticationResponse.refreshToken.token);
				return true;
			}
			return false;
		}
		return false;
	},

	logout(go: boolean = true): void {
		// Usa il composable moderno per lo store Pinia
		const authStore = useAuthStore();

		// Usa le actions Pinia invece delle mutations Vuex
		authStore.setAccessToken(null);
		authStore.setRefreshToken(null);
		authStore.setUserInfo(null);

		if (go) {
			// Mantiene il factory per il router (composable non funziona nei service)
			const router = routerFactory();
			router.push({ name: 'login___it' });
			router.go(0);
		}
	},

	async sendResetPassword(username: string): Promise<void> {
		return (await authEndpoints.sendResetPassword(username)).data;
	},

	async resetPassword(data: ResetPasswordServiceData): Promise<void> {
		// Converte i dati del form nel formato richiesto dall'endpoint
		const resetData: ResetPasswordModel = {
			username: data.username,
			token: data.token,
			newPassword: data.newPassword,
		};

		// Validazione runtime dell'input (attiva solo in development)
		zodValidator.validateSingle(resetData, ResetPasswordModelSchema, 'ResetPasswordModel.input');

		return (await authEndpoints.resetPassword(resetData)).data;
	},

	async getUserName(key: string): Promise<string> {
		const response = (await authEndpoints.getUserName(key)).data;
		// Validazione runtime della response (attiva solo in development)
		// Note: getUserName returns a simple string, no schema validation needed
		return response;
	},

	async changePassword(payload: ChangePasswordServiceData): Promise<void> {
		// Converte i dati del form nel formato richiesto dall'endpoint
		const passwordData: ChangePasswordModel = {
			oldPassword: payload.oldPassword,
			newPassword: payload.newPassword,
		};

		// Validazione runtime dell'input (attiva solo in development)
		zodValidator.validateSingle(
			passwordData,
			ChangePasswordModelSchema,
			'ChangePasswordModel.input',
		);

		return (await authEndpoints.changePassword(passwordData)).data;
	},

	async impersonate(workerId: string): Promise<void> {
		const userInfo = (await authEndpoints.getImpersonatedUserInfo(workerId)).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateSingle(
			userInfo,
			ApplicationUserDtoSchema,
			'ApplicationUserDto.getImpersonatedUserInfo',
		);

		if (userInfo) {
			// Usa il composable moderno per lo store Pinia
			const authStore = useAuthStore();
			authStore.setImpersonatedUserInfo(userInfo);
		}
	},

	unImpersonate(): void {
		// Usa il composable moderno per lo store Pinia
		const authStore = useAuthStore();
		authStore.setImpersonatedUserInfo(null);
	},

	async getUserInfo(): Promise<void> {
		try {
			const userInfo = (await authEndpoints.getUserInfo()).data;
			// Validazione runtime della response (attiva solo in development)
			zodValidator.validateSingle(
				userInfo,
				ApplicationUserDtoSchema,
				'ApplicationUserDto.getUserInfo',
			);

			if (userInfo) {
				// Usa il composable moderno per lo store Pinia
				const authStore = useAuthStore();
				authStore.setUserInfo(userInfo);
			}
		} catch {
			this.logout();
		}
	},
};

export default authService;
